import { PUBLIC_API_BASE_URL, PUBLIC_COMPANY_ID } from './publicApi';

export type ShowcaseEventType = 'page_view' | 'vehicle_view' | 'whatsapp_click' | 'share';

const VISITOR_KEY = 'vitrine_visitor_id';
const SESSION_KEY = 'vitrine_session';
const UTM_KEY = 'vitrine_utm';
const SESSION_IDLE_MS = 30 * 60 * 1000;

const isLocalHost = () => {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.startsWith('127.');
};

// Em localhost a vitrine usa a API de produção: não contamos esses acessos.
const trackingEnabled = () => !isLocalHost() || process.env.REACT_APP_ANALYTICS_DEV === 'true';

const randomId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* segue para o fallback */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const safeGet = (storage: Storage, key: string): string | null => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (storage: Storage, key: string, value: string) => {
  try {
    storage.setItem(key, value);
  } catch {
    /* modo privado / storage bloqueado */
  }
};

let memoryVisitorId: string | null = null;
let memorySession: { id: string; last: number } | null = null;

const getVisitorId = (): string => {
  const stored = safeGet(localStorage, VISITOR_KEY);
  if (stored) return stored;
  memoryVisitorId = memoryVisitorId || randomId();
  safeSet(localStorage, VISITOR_KEY, memoryVisitorId);
  return memoryVisitorId;
};

/** Sessão = visita; expira após 30 min sem atividade. */
const getSessionId = (): string => {
  const now = Date.now();
  let session = memorySession;
  const raw = safeGet(sessionStorage, SESSION_KEY);
  if (raw) {
    try {
      session = JSON.parse(raw);
    } catch {
      session = null;
    }
  }
  if (!session || now - session.last > SESSION_IDLE_MS) {
    session = { id: randomId(), last: now };
  }
  session.last = now;
  memorySession = session;
  safeSet(sessionStorage, SESSION_KEY, JSON.stringify(session));
  return session.id;
};

type Utm = { utm_source?: string; utm_medium?: string; utm_campaign?: string };

/** Guarda a campanha (?utm_source=instagram...) da primeira página da visita. */
const getUtm = (): Utm => {
  const params = new URLSearchParams(window.location.search);
  const fromUrl: Utm = {};
  (['utm_source', 'utm_medium', 'utm_campaign'] as const).forEach((k) => {
    const v = params.get(k);
    if (v) fromUrl[k] = v.slice(0, 100);
  });
  if (Object.keys(fromUrl).length > 0) {
    safeSet(sessionStorage, UTM_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  try {
    return JSON.parse(safeGet(sessionStorage, UTM_KEY) || '{}');
  } catch {
    return {};
  }
};

/** Origem externa (Instagram, Google...) só na primeira página da visita. */
let referrerSent = false;

export const trackShowcaseEvent = (eventType: ShowcaseEventType, vehicleId?: number | string | null) => {
  if (!trackingEnabled()) return;
  try {
    const referrer = !referrerSent ? document.referrer || null : null;
    referrerSent = true;
    const id = vehicleId != null && Number(vehicleId) > 0 ? Number(vehicleId) : null;

    void fetch(`${PUBLIC_API_BASE_URL}public/analytics/events`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'company-x': String(PUBLIC_COMPANY_ID),
      },
      body: JSON.stringify({
        event_type: eventType,
        vehicle_id: id,
        visitor_id: getVisitorId(),
        session_id: getSessionId(),
        path: window.location.pathname.slice(0, 255),
        referrer,
        ...getUtm(),
      }),
    }).catch(() => undefined);
  } catch {
    /* estatística nunca pode quebrar a vitrine */
  }
};
